import { PERMISSIONS } from '../constants/permissions';

/** Everything landingPathFor needs; RoleLanding builds it from AuthContext + DashboardAccess. */
export interface LandingContext {
    isSystemAdmin: boolean;
    /** SaaS Operator platform user (no institution of its own): keeps the module launcher. */
    isPlatformOperator?: boolean;
    /** Institution admin (wildcard role) in the selected institution. */
    isAdmin: boolean;
    /** True when the user heads a department (JWT `isDepartmentAdmin` claim). */
    isDepartmentAdmin?: boolean;
    has: (key: string) => boolean;
    /** Institution has the gated module enabled (merchandising, commercial, ...). */
    moduleEnabled: (module: string) => boolean;
    /**
     * A module deep-board is reachable: enabled for the tenant AND the role holds a widget in it
     * (the same `visibleModules` set the sidebar's `dashboard.*` gates use). Defaults to
     * moduleEnabled when not supplied.
     */
    boardVisible?: (module: string) => boolean;
}

export const HOME_PATH = '/';

/**
 * The Tiles pages a role landing can redirect to instead of '/', per Thread-enabled home group. The Tiles/Thread switch
 * also shows on these pages (for that group only), and picking Thread there goes back to '/'. The board group lands on '/'.
 */
export const TILES_PAGES_BY_GROUP: Readonly<Record<string, readonly string[]>> = {
    merch: ['/merchandising/dashboard'],
    commercial: ['/commercial/dashboard'],
    people: ['/dashboard/people'],
    store: ['/dashboard/inventory'],
    sales: ['/dashboard/sales'],
    salesRep: ['/dashboard/sales'],
    personal: ['/dashboard/attendance'],
};

export const isTilesPageOf = (group: string | null, pathname: string): boolean =>
    group !== null && (TILES_PAGES_BY_GROUP[group] ?? []).includes(pathname);

interface Rule {
    /** Does this rule apply to the user (role signal)? */
    matches: (c: LandingContext) => boolean;
    path: string;
    /** The SAME gate the target route / menu entry uses; when false we fall through. */
    gate: (c: LandingContext) => boolean;
}

const boardGate = (module: string) => (c: LandingContext) =>
    c.has(PERMISSIONS.DASHBOARD_VIEW) && (c.boardVisible ?? c.moduleEnabled)(module);

/**
 * Ordered rules, first match wins. A rule whose role signal matches but whose target gate fails
 * falls through to the next rule and ultimately to '/', so nobody lands on a 403/redirect.
 * Gates mirror App.tsx: /dashboard/* = PermissionRoute DASHBOARD_VIEW (+ sidebar's module
 * visibility), merchandising dashboard = MERCH_REPORT_VIEW, commercial dashboard =
 * COMMERCIAL_REPORT_VIEW (both also need the module enabled for the tenant).
 */
const RULES: Rule[] = [
    { matches: c => c.isSystemAdmin, path: '/', gate: () => true },
    { matches: c => c.isPlatformOperator === true, path: '/modules', gate: () => true },
    { matches: c => c.isAdmin, path: '/', gate: () => true },
    { matches: c => c.has(PERMISSIONS.DASHBOARD_VIEW_MANAGER), path: '/', gate: () => true },
    // Accountant: the home board is the finance preset plus the Action Center (claims, receivables).
    { matches: c => c.has(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT), path: '/', gate: () => true },
    // Finance role boards (AP clerk, AR clerk, payment approver, auditor): same home board, own preset.
    {
        matches: c => c.has(PERMISSIONS.DASHBOARD_VIEW_PAYABLES) || c.has(PERMISSIONS.DASHBOARD_VIEW_RECEIVABLES)
            || c.has(PERMISSIONS.DASHBOARD_VIEW_PAYMENT_APPROVAL) || c.has(PERMISSIONS.DASHBOARD_VIEW_AUDIT),
        path: '/', gate: () => true,
    },
    { matches: c => c.has(PERMISSIONS.USER_MANAGE), path: '/dashboard/people', gate: boardGate('people') },
    { matches: c => c.has(PERMISSIONS.DASHBOARD_INVENTORY_VIEW), path: '/dashboard/inventory', gate: boardGate('inventory') },
    { matches: c => c.has(PERMISSIONS.QUOTATION_MANAGE), path: '/dashboard/sales', gate: boardGate('sales') },
    // Commercial before merchandising: the commercial manager also holds MERCH_REPORT_VIEW (to read
    // the order book behind its LCs), while the merchandiser holds no commercial report key.
    {
        matches: c => c.has(PERMISSIONS.COMMERCIAL_REPORT_VIEW),
        path: '/commercial/dashboard',
        gate: c => c.has(PERMISSIONS.COMMERCIAL_REPORT_VIEW) && c.moduleEnabled('commercial'),
    },
    {
        matches: c => c.has(PERMISSIONS.MERCH_REPORT_VIEW),
        path: '/merchandising/dashboard',
        gate: c => c.has(PERMISSIONS.MERCH_REPORT_VIEW) && c.moduleEnabled('merchandising'),
    },
    // Currently never taken in practice: attendance widgets are gated on DASHBOARD_VIEW_MANAGER
    // (registry/attendanceWidgets.tsx), and a manager already matched the '/' rule above. Kept so a
    // department-scoped attendance board lands here once one exists; the gate keeps it safe until then.
    // Do NOT grant DEPARTMENT_HEAD DASHBOARD_VIEW to "activate" it: the dashboard endpoints are
    // institution-wide, not department-scoped.
    { matches: c => c.isDepartmentAdmin === true, path: '/dashboard/attendance', gate: boardGate('attendance') },
];

export function landingPathFor(ctx: LandingContext): string {
    for (const rule of RULES) {
        if (rule.matches(ctx) && rule.gate(ctx)) return rule.path;
    }
    return HOME_PATH;
}

/** Post-login entry route: RoleLanding resolves it to landingPathFor(...). */
export const LANDING_ROUTE = '/home';

/**
 * Paths that are not a deep link worth honouring after login: '/login' would bounce straight back,
 * and '/' is where a logged-out visitor to the site root gets recorded as "from" by the route
 * guard -- honouring it would skip the role landing for the most common way people arrive.
 */
const NON_TARGETS = ['/login', '/'];

/**
 * Sanitizes a post-login "return to" value (ProtectedRoute's `state.from` string, a
 * PermissionRoute/SystemAdminRoute `state.from` Location object, or the auth interceptor's
 * `?from=` query value). Only a same-origin, app-relative path survives: it must start with a
 * single '/', not '//' or '/\' (protocol-relative -> another origin), contain no control
 * characters or backslashes, and not be the login page itself. Anything else returns null.
 */
export function safeReturnPath(raw: unknown): string | null {
    let path: string | null = null;
    if (typeof raw === 'string') {
        path = raw;
    } else if (raw && typeof raw === 'object' && typeof (raw as { pathname?: unknown }).pathname === 'string') {
        const loc = raw as { pathname: string; search?: unknown; hash?: unknown };
        path = loc.pathname
            + (typeof loc.search === 'string' ? loc.search : '')
            + (typeof loc.hash === 'string' ? loc.hash : '');
    }
    if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\\\u0000-\u001f\u007f]/.test(path)) return null;
    const pathname = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    if (NON_TARGETS.includes(pathname.toLowerCase())) return null;
    return path;
}
